import React, { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

/**
 * Quick-reference FAQ shown on the Garage page so players have somewhere
 * obvious to learn how the new mechanics work without a full tutorial
 * popup hijacking their first race.
 *
 * Each section is collapsible. Add new entries here when you ship new
 * mechanics — the Garage is the natural "between-race" moment to read it.
 */
const FAQS = [
  {
    q: "How do I activate my chassis ULTIMATE?",
    a: [
      "Each chassis has a once-per-race signature move:",
      "• Starter Cruiser → WHEEL POPOFF (4 spinning tires fire outward)",
      "• Knight Hauler → SPIKE BURST (expanding metal-spike ring)",
      "• M1 Bruiser → TITAN MODE (tank triples in size for 4 s)",
      "• Toonster → HULK FIST (cartoon fist drops, sends cops flying)",
      "• Lightning Wedge → FLAME WAKE (4 s of flame cone behind you)",
      "",
      "Trigger condition: the track must be free of live cops/FBI for 5 seconds. Watch the HUD pill — when it reads 'READY', press U or Enter to fire.",
    ],
  },
  {
    q: "How do I unlock a SIDEKICK?",
    a: [
      "Collect 2 artifacts (correctly answer the trivia) within 10 seconds of each other in any race.",
      "On your NEXT race you'll get a 30-second pre-race designer to build a custom AI buddy: pick a chassis, paint it, name it, and DRAW pixel-art decals on the 24×12 grid (use +/− to size the brush).",
      "The sidekick will follow you, ram cops in your path, and — if you get jailed — drive to the police station and break you out.",
      "Sidekicks take damage and explode at 0 HP, so keep them out of missile crossfire.",
    ],
  },
  {
    q: "How does the COIN economy work?",
    a: [
      "You earn coins by finishing races, collecting all artifacts, and beating your tourism record on each city.",
      "Spend them in the Garage on:",
      "• Performance — Rocket Engine (top-end speed +1, max 5), Reinforced Armor (+25 max HP, max 5)",
      "• Weapons — Missile packs (3 or 8), Rocket Boosts, Trunk Monkeys",
      "• Maintenance — Full Repair (cost scales with damage)",
      "• Cosmetics — Premium Paints + Exhaust Trails (one-time unlock, then free to swap)",
    ],
  },
  {
    q: "What does the BLUE SPEED GATE do?",
    a: [
      "Driving through one triggers a power burst: your tires glow, you get a speed boost, and you become INTANGIBLE to cops/FBI for the duration. Phase straight through them — they fly sideways instead of arresting you. Use it to escape pursuit or punch into a tight spot.",
    ],
  },
  {
    q: "How do I escape JAIL?",
    a: [
      "Three options when arrested:",
      "1. Wait it out — the countdown releases you automatically.",
      "2. Press E to launch a Trunk Monkey if you have one — instant breakout + AoE damage.",
      "3. Click 'SELL ARTIFACT' on the right panel — costs 1 collected artifact but frees you immediately.",
      "If your sidekick is alive, it auto-drives to the cell and breaks you out — no input needed.",
    ],
  },
  {
    q: "What happens when I MISSILE a cop or FBI?",
    a: [
      "Missiles do 50 dmg vs cops, 35 vs armored FBI. When their HP hits zero, they explode and are PERMANENTLY removed from the race — they won't respawn. Same for monkey-bomb AoE kills. Drive past the scorch mark and feel proud.",
    ],
  },
  {
    q: "Can I change my CATCHPHRASE?",
    a: [
      "Yes — set or update it on the Hub (dashboard). It pops over your car for 2.5 s every time you correctly answer a trivia question. Keep it clean: profanity is auto-blocked, repeated attempts result in a 3-strike ban from setting catchphrases.",
    ],
  },
  {
    q: "FULLSCREEN + keyboard shortcuts",
    a: [
      "Press F (or click FULL in the top-right of the race HUD) to enter fullscreen. Esc exits.",
      "",
      "In-race keys:",
      "• WASD / arrows — drive",
      "• Space — fire missile",
      "• Shift — rocket boost",
      "• E — launch Trunk Monkey",
      "• U — fire your chassis ultimate (Enter also works)",
      "• M — mute/unmute",
      "• F — toggle fullscreen",
      "• V — toggle map / cockpit view",
    ],
  },
  {
    q: "Found a bug or have an idea?",
    a: [
      "Click the red 'How can we be better?' button (bottom-left of every screen) — pick a category, type a tip, send. We read every one.",
    ],
  },
];

export default function GarageFAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="surface-solid p-6" data-testid="garage-faq">
      <div className="flex items-center gap-3 mb-5">
        <HelpCircle className="w-5 h-5 text-[#FFCC00]" />
        <div>
          <div className="label-kicker">Drivers&apos; Manual</div>
          <div className="font-heading text-2xl">FAQ</div>
        </div>
      </div>
      <div className="border border-white/10 divide-y divide-white/5">
        {FAQS.map((entry, i) => {
          const open = openIdx === i;
          return (
            <div key={i} data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpenIdx(open ? -1 : i)}
                data-testid={`faq-toggle-${i}`}
                aria-expanded={open}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  open ? "bg-[#FFCC00]/[0.06] text-[#FFCC00]" : "hover:bg-white/[0.03]"
                }`}
              >
                <span className="font-heading text-sm uppercase tracking-wider flex-1 leading-tight">
                  {entry.q}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
                  strokeWidth={2.5}
                />
              </button>
              {open && (
                <div className="px-5 pb-5 pt-1 text-sm text-white/70 leading-relaxed space-y-1.5">
                  {entry.a.map((line, j) =>
                    line === "" ? (
                      <div key={j} className="h-2" />
                    ) : (
                      <div key={j}>{line}</div>
                    ),
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
